import type {
  CreateCallOffMutationOptions,
  CreateCallOffPayload,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCallOff } from '../queryKeys';

import { createCallOff } from './createCallOff';

/**
 * Custom hook for creating a new call-off.
 * The call-off starts in DRAFT status with optional line items.
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/create_calloff_calloffs_post
 */
export const useMutationCreateCallOff = (
  options?: CreateCallOffMutationOptions,
) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...rest } = options ?? {};

  return useMutation({
    mutationFn: async (payload: CreateCallOffPayload) => createCallOff(payload),
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.getAll(undefined),
      });

      return onSuccess?.(...args);
    },
    ...rest,
  });
};
